import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../utils/api";
import { Clock, Plus, Trash2, Play, ArrowLeft, Power, CheckCircle, XCircle, Crown } from "lucide-react";

const KIND_OPTS = [
  { value: "chain_template", label: "Chain template (recommended)" },
  { value: "chain",          label: "Chain run — full payload" },
  { value: "multi",          label: "Multi-file run — full payload" },
  { value: "single",         label: "Single-file run — full payload" },
];

const CRON_PRESETS = [
  { label: "Every hour (top of the hour)",    value: "0 * * * *" },
  { label: "Every day at 09:00 UTC",          value: "0 9 * * *" },
  { label: "Every weekday at 09:00 UTC",      value: "0 9 * * 1-5" },
  { label: "Every Monday at 09:00 UTC",       value: "0 9 * * 1" },
  { label: "1st of every month at 09:00 UTC", value: "0 9 1 * *" },
];

function Badge({ ok, text }) {
  return (
    <span style={{ padding: "0.15rem 0.55rem", borderRadius: "0.3rem", fontSize: "0.72rem", fontWeight: 600,
                   background: ok ? "#dcfce7" : "#fee2e2", color: ok ? "#166534" : "#991b1b",
                   display: "inline-flex", alignItems: "center", gap: "0.2rem" }}>
      {ok ? <CheckCircle size={10} /> : <XCircle size={10} />} {text}
    </span>
  );
}

export default function SchedulesPage() {
  const [rows, setRows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [isPaid, setIsPaid] = useState(true); // default optimistic; actual gating via plan check
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [kind, setKind] = useState("chain_template");
  const [cron, setCron] = useState("0 9 1 * *");
  const [templateId, setTemplateId] = useState("");
  const [rawPayload, setRawPayload] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const load = () => {
    api.get("/schedules").then(r => setRows(r.data || [])).catch(() => setRows([]));
    api.get("/chain-templates").then(r => setTemplates(r.data || [])).catch(() => setTemplates([]));
    // Plan check — lookup current department subscription (if any)
    api.get("/billing/subscription/status").then(r => {
      const planName = (r.data?.plan?.name || "").toLowerCase();
      setIsPaid(planName.includes("close automation") || planName.includes("enterprise") || planName.includes("trial"));
    }).catch(() => setIsPaid(true));
  };

  useEffect(() => { load(); }, []);

  const onCreate = async () => {
    setError("");
    let payload = {};
    if (kind === "chain_template") {
      if (!templateId) { setError("Pick a chain template."); return; }
      payload = { template_id: templateId };
    } else {
      try { payload = rawPayload.trim() ? JSON.parse(rawPayload) : {}; }
      catch (e) { setError(`Payload JSON invalid: ${e.message}`); return; }
    }
    try {
      await api.post("/schedules", { name: name.trim(), kind, cron, payload, active: true });
      setCreating(false); setName(""); setTemplateId(""); setRawPayload("");
      load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const onToggleActive = async (s) => {
    try { await api.put(`/schedules/${s.id}`, { active: !s.active }); load(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const onDelete = async (s) => {
    if (!window.confirm(`Delete schedule "${s.name}"?`)) return;
    try { await api.delete(`/schedules/${s.id}`); load(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const onTriggerNow = async (s) => {
    if (!window.confirm(`Run "${s.name}" now?`)) return;
    try { await api.post(`/schedules/${s.id}/trigger`); alert("Triggered! Check Run History in a moment."); load(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  return (
    <Layout>
      <div className="animate-in" style={{ maxWidth: "1080px", margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.25rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><ArrowLeft size={18} /></button>
            <div>
              <h1 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.5rem", margin: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Clock size={18} style={{ color: "#8B5CF6" }} /> Schedules
              </h1>
              <p style={{ color: "var(--text-muted)", fontSize: "0.82rem", margin: 0 }}>
                Set it once — ReconX runs it on your cadence and emails you the result.
              </p>
            </div>
          </div>
          <button data-testid="schedule-add-btn" onClick={() => setCreating(!creating)} className="btn-primary"
            style={{ fontSize: "0.85rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <Plus size={14} /> New schedule
          </button>
        </div>

        {!isPaid && (
          <div data-testid="upsell-close-automation" className="recon-card" style={{ padding: "1rem 1.25rem", marginBottom: "1rem", background: "linear-gradient(135deg, #F3E8FF 0%, #EDE9FE 100%)", border: "1px solid #C4B5FD", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem" }}>
            <div>
              <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#6D28D9", textTransform: "uppercase", letterSpacing: "0.06em", display: "flex", alignItems: "center", gap: "0.3rem", marginBottom: "0.3rem" }}>
                <Crown size={12} /> Close Automation
              </div>
              <div style={{ fontSize: "0.9rem", color: "#4C1D95", fontWeight: 500 }}>
                Schedules + email + PDF delivered to inbox · €249/mo
              </div>
            </div>
            <button data-testid="upsell-cta" onClick={() => navigate("/pricing")} className="btn-primary"
              style={{ background: "#8B5CF6", borderColor: "#8B5CF6", fontSize: "0.82rem" }}>
              View plans →
            </button>
          </div>
        )}

        {creating && (
          <div data-testid="schedule-create-panel" className="recon-card" style={{ padding: "1.25rem", marginBottom: "1rem" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "0.75rem" }}>
              <div>
                <label style={{ fontSize: "0.72rem", fontWeight: 600, display: "block", marginBottom: "0.25rem" }}>Name</label>
                <input data-testid="schedule-name" value={name} onChange={e => setName(e.target.value)}
                  placeholder="e.g. Monthly close — PO→INV→PAY" className="recon-input" style={{ width: "100%", fontSize: "0.9rem" }} />
              </div>
              <div>
                <label style={{ fontSize: "0.72rem", fontWeight: 600, display: "block", marginBottom: "0.25rem" }}>Kind</label>
                <select data-testid="schedule-kind" value={kind} onChange={e => setKind(e.target.value)}
                  className="recon-input" style={{ width: "100%", fontSize: "0.9rem" }}>
                  {KIND_OPTS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: "0.75rem" }}>
              <label style={{ fontSize: "0.72rem", fontWeight: 600, display: "block", marginBottom: "0.25rem" }}>Cron (UTC)</label>
              <div style={{ display: "flex", gap: "0.4rem", alignItems: "center", flexWrap: "wrap" }}>
                <input data-testid="schedule-cron" value={cron} onChange={e => setCron(e.target.value)}
                  className="recon-input" style={{ fontSize: "0.9rem", minWidth: "180px", fontFamily: "JetBrains Mono, monospace" }} />
                <select data-testid="schedule-cron-preset" onChange={e => e.target.value && setCron(e.target.value)}
                  className="recon-input" style={{ fontSize: "0.82rem" }}>
                  <option value="">Presets…</option>
                  {CRON_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            </div>

            {kind === "chain_template" && (
              <div style={{ marginBottom: "0.75rem" }}>
                <label style={{ fontSize: "0.72rem", fontWeight: 600, display: "block", marginBottom: "0.25rem" }}>Chain template</label>
                <select data-testid="schedule-template" value={templateId} onChange={e => setTemplateId(e.target.value)}
                  className="recon-input" style={{ width: "100%", fontSize: "0.9rem" }}>
                  <option value="">— pick —</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                {templates.length === 0 && (
                  <div style={{ fontSize: "0.76rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>
                    No templates yet — <button onClick={() => navigate("/chain-reconcile")} style={{ background: "none", border: "none", color: "var(--sage)", cursor: "pointer", padding: 0 }}>create one first</button>.
                  </div>
                )}
              </div>
            )}
            {kind !== "chain_template" && (
              <div style={{ marginBottom: "0.75rem" }}>
                <label style={{ fontSize: "0.72rem", fontWeight: 600, display: "block", marginBottom: "0.25rem" }}>Payload (JSON)</label>
                <textarea data-testid="schedule-payload" value={rawPayload} onChange={e => setRawPayload(e.target.value)}
                  placeholder={'{"name":"…","anchor_files":[…],"legs":[…]}'} rows={6}
                  className="recon-input" style={{ width: "100%", fontSize: "0.82rem", fontFamily: "JetBrains Mono, monospace" }} />
                <div style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>
                  Tip: run once interactively, then copy the request body from the network tab.
                </div>
              </div>
            )}

            {error && <div style={{ color: "#991b1b", fontSize: "0.82rem", marginBottom: "0.5rem" }}>{error}</div>}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.4rem" }}>
              <button onClick={() => setCreating(false)} className="btn-secondary" style={{ fontSize: "0.85rem" }}>Cancel</button>
              <button data-testid="schedule-save" onClick={onCreate} className="btn-primary"
                disabled={!name.trim()} style={{ fontSize: "0.85rem", opacity: name.trim() ? 1 : 0.5 }}>
                Save schedule
              </button>
            </div>
          </div>
        )}

        <div className="recon-card" style={{ padding: "1rem 1.25rem" }}>
          {rows.length === 0 && !creating && (
            <div data-testid="schedules-empty" style={{ padding: "2rem", textAlign: "center", color: "var(--text-muted)" }}>
              No schedules yet. Click <b>New schedule</b> above.
            </div>
          )}
          {rows.length > 0 && (
            <table data-testid="schedules-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
              <thead>
                <tr>
                  {["Name", "Kind", "Cron (UTC)", "Last run", "Status", "Actions"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "0.5rem 0.75rem", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-muted)", fontWeight: 600, borderBottom: "1px solid #e5e7eb" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(s => (
                  <tr key={s.id} data-testid={`schedule-row-${s.id}`} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "0.6rem 0.75rem", fontWeight: 500 }}>{s.name}</td>
                    <td style={{ padding: "0.6rem 0.75rem", fontSize: "0.78rem", color: "var(--text-muted)" }}>{s.kind}</td>
                    <td style={{ padding: "0.6rem 0.75rem", fontFamily: "JetBrains Mono, monospace", fontSize: "0.78rem" }}>{s.cron}</td>
                    <td style={{ padding: "0.6rem 0.75rem", fontSize: "0.78rem", color: "var(--text-muted)" }}>
                      {s.last_run_at ? new Date(s.last_run_at).toLocaleString() : "—"}
                    </td>
                    <td style={{ padding: "0.6rem 0.75rem" }}>
                      {s.active
                        ? (s.last_status === "failed"
                            ? <Badge ok={false} text="Last run failed" />
                            : <Badge ok={true} text="Active" />)
                        : <span style={{ padding: "0.15rem 0.55rem", borderRadius: "0.3rem", fontSize: "0.72rem", fontWeight: 600, background: "#f3f4f6", color: "#6b7280" }}>Paused</span>}
                    </td>
                    <td style={{ padding: "0.6rem 0.75rem" }}>
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <button data-testid={`schedule-trigger-${s.id}`} onClick={() => onTriggerNow(s)} title="Run now"
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#16a34a" }}>
                          <Play size={14} />
                        </button>
                        <button data-testid={`schedule-toggle-${s.id}`} onClick={() => onToggleActive(s)} title={s.active ? "Pause" : "Resume"}
                          style={{ background: "none", border: "none", cursor: "pointer", color: s.active ? "#a16207" : "#16a34a" }}>
                          <Power size={14} />
                        </button>
                        <button data-testid={`schedule-del-${s.id}`} onClick={() => onDelete(s)} title="Delete"
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626" }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
}
